import { z } from "zod";

/**
 * Upper bound on a custom reporting window. Each request expands into two
 * equal-length aggregations (current + previous) plus a per-day trend query,
 * so an unbounded span lets one URL trigger an arbitrarily expensive scan.
 * One leap year is the widest window the UI can meaningfully chart.
 */
export const MAX_REPORTING_RANGE_DAYS = 366;

const MS_PER_DAY = 86_400_000;

export const reportingDateRangeQueryShape = {
  startDate: z.string().date().optional(),
  endDate: z.string().date().optional(),
};

export function validateReportingDateRange(
  value: { startDate?: string; endDate?: string },
  ctx: z.RefinementCtx,
): void {
  if (Boolean(value.startDate) !== Boolean(value.endDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: value.startDate ? ["endDate"] : ["startDate"],
      message: "startDate and endDate must be provided together",
    });
  }
  if (!value.startDate || !value.endDate) return;

  if (value.endDate < value.startDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["endDate"],
      message: "endDate must be on or after startDate",
    });
    return;
  }

  const spanDays =
    Math.round(
      (Date.parse(`${value.endDate}T00:00:00.000Z`) -
        Date.parse(`${value.startDate}T00:00:00.000Z`)) /
        MS_PER_DAY,
    ) + 1;
  if (spanDays > MAX_REPORTING_RANGE_DAYS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["endDate"],
      message: `date range must not exceed ${MAX_REPORTING_RANGE_DAYS} days`,
    });
  }
}
